import { useLingui } from "@lingui/react/macro";
import { computerStateLabel } from "@rakazo/contracts";
import {
  Button,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@rakazo/ui-web";
import { ArrowUpRight, Laptop, Monitor, Network, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { createSpaceRpc, selectedSpaceId, selectSpace } from "../lib/rpc";
import { useWorkspace } from "../lib/use-workspace";
import { WorkspaceHandoff } from "./WorkspaceHandoff";

/** Read-only system tray. Opening it never wakes a machine or acquires control. */
export function WorkspaceControls({
  onOpen,
  resumePath = "/app",
}: {
  onOpen: (path: string) => void;
  resumePath?: string;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const client = useMemo(() => createSpaceRpc(selectedSpaceId()), []);
  const spaces = useWorkspace(
    useCallback(() => client.spaces.list(), [client]),
    30000,
    open,
  );
  const computers = useWorkspace(
    useCallback(() => client.workspace.computers(), [client]),
    15000,
    open,
  );
  const system = useWorkspace(
    useCallback(() => client.workspace.system(), [client]),
    15000,
    open,
  );
  function visit(path: string) {
    setOpen(false);
    onOpen(path);
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger aria-label={t`Workspace controls`} title={t`Workspace controls`}>
        <Network size={19} />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={16}
        className="w-80 max-w-[calc(100vw-24px)] gap-4 p-4"
      >
        <div className="flex items-center justify-between gap-3">
          <PopoverTitle>{t`Workspace`}</PopoverTitle>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Refresh status`}
            onClick={() => {
              system.refresh();
              computers.refresh();
              spaces.refresh();
            }}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
        {spaces.data && (
          <NativeSelect
            aria-label={t`Space`}
            value={spaces.data.current.id}
            onChange={(event) => {
              if (event.target.value === spaces.data?.current.id) return;
              if (!selectSpace(event.target.value)) {
                setError(t`Could not switch space`);
                return;
              }
              visit("/app");
            }}
          >
            {spaces.data.spaces.map((space) => (
              <NativeSelectOption key={space.id} value={space.id}>
                {space.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        )}
        {(error || spaces.error) && (
          <p role="alert" className="text-xs text-destructive">
            {error || spaces.error}
          </p>
        )}
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-muted/40"
          onClick={() => visit("/app/workspace/system")}
        >
          <Network size={18} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium">{t`Server`}</span>
            <span
              role="status"
              className={`mt-1 block text-xs ${system.error || system.data?.database === "unavailable" ? "text-destructive" : "text-muted-foreground"}`}
            >
              {system.error || system.data?.database === "unavailable"
                ? t`Unavailable`
                : system.data
                  ? t`Connected`
                  : t`Checking…`}
            </span>
          </span>
          <ArrowUpRight size={14} className="text-muted-foreground" />
        </button>
        <div className="max-h-[35vh] overflow-y-auto">
          <div className="mb-1 flex items-center justify-between gap-3 px-2">
            <span className="text-xs text-muted-foreground">{t`Computers`}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => visit("/app/workspace/computers")}
            >{t`Manage`}</Button>
          </div>
          {computers.error ? (
            <p role="alert" className="px-2 text-xs text-destructive">
              {computers.error}
            </p>
          ) : !computers.data ? (
            <p role="status" className="px-2 text-xs text-muted-foreground">{t`Loading…`}</p>
          ) : !computers.data.length ? (
            <p className="px-2 text-xs text-muted-foreground">{t`No computers connected`}</p>
          ) : (
            computers.data.map((machine) => {
              const Icon = machine.device ? Laptop : Monitor;
              return (
                <button
                  key={machine.id}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-muted/40"
                  onClick={() =>
                    visit(`/app/workspace/computers?computer=${encodeURIComponent(machine.id)}`)
                  }
                >
                  <Icon size={18} className="shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">{machine.name}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {computerStateLabel(machine)}
                    </span>
                  </span>
                  <ArrowUpRight size={14} className="shrink-0 text-muted-foreground" />
                </button>
              );
            })
          )}
        </div>
        <WorkspaceHandoff path={resumePath} />
      </PopoverContent>
    </Popover>
  );
}
